/**
 * Friendly 404 page.
 *
 * Replaces the previous silent redirect to home so that:
 *   1. Visitors who follow a broken link get an actual orientation point
 *      with links back into the site (Home, Services, Contact).
 *   2. Search engines that hit a non-existent URL see a real "noindex"
 *      response page instead of a same-content soft-404 redirect (Google
 *      treats soft-404s as a crawl-budget waste).
 *
 * Note on HTTP status:
 *   This is a pure SPA so we can't return a real 404 status code from the
 *   client — but emitting `<meta name="robots" content="noindex">` via
 *   `SEO` is the next-best signal to crawlers that this URL is dead.
 */
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Home, MailOpen, Compass } from "lucide-react";
import SEO from "@/components/SEO";

const DEFAULT_LANG = "en";

export default function NotFoundPage() {
  const params = useParams();
  const lang = params.lang === "ja" ? "ja" : DEFAULT_LANG;
  const isJa = lang === "ja";

  const links = [
    {
      to: `/${lang}`,
      label: isJa ? "ホーム" : "Home",
      Icon: Home,
      desc: isJa ? "トップページへ" : "Back to the main page",
    },
    {
      to: `/${lang}/services`,
      label: isJa ? "サービス" : "Services",
      Icon: Compass,
      desc: isJa ? "運営中の3サービス" : "Our three live services",
    },
    {
      to: `/${lang}/contact`,
      label: isJa ? "お問い合わせ" : "Contact",
      Icon: MailOpen,
      desc: isJa ? "サポートまで" : "Get in touch with support",
    },
  ];

  return (
    <article className="bg-white min-h-[70vh]" data-testid="not-found-page">
      <SEO
        title={
          isJa
            ? "404 — ページが見つかりません | NoJo Enterprise"
            : "404 — Page Not Found | NoJo Enterprise"
        }
        description={
          isJa
            ? "お探しのページは見つかりませんでした。"
            : "The page you were looking for could not be found."
        }
        path={`/${lang}/404`}
        lang={lang}
        noindex
      />

      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div
          className="font-mono-display text-[12px] tracking-[0.28em] uppercase font-bold mb-4 text-[#5B2E83]"
          data-testid="not-found-code"
        >
          404
        </div>
        <h1
          className={[
            isJa ? "font-ja" : "font-display",
            "text-[#0F1D2D] text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] mb-5",
          ].join(" ")}
        >
          {isJa
            ? "お探しのページは見つかりませんでした。"
            : "We couldn't find the page you were looking for."}
        </h1>
        <p className="text-[15px] md:text-base leading-[1.75] text-[#475569] mb-10 max-w-2xl">
          {isJa
            ? "URLが変更されたか、削除された可能性があります。下のリンクからNoJo Enterpriseの主要ページに移動できます。"
            : "The link may be out of date, or the page may have been removed. Here are a few good places to pick up the trail."}
        </p>

        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {links.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                data-testid={`not-found-link-${l.label.toLowerCase()}`}
                className="group relative block h-full rounded-2xl border border-[#E2E8F0] bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-22px_rgba(15,29,45,0.25)] hover:border-transparent"
              >
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-[#5B2E83]/10">
                  <l.Icon className="h-4 w-4 text-[#5B2E83]" strokeWidth={1.7} />
                </span>
                <div
                  className={[
                    "mt-3",
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-[16px] font-bold tracking-tight",
                  ].join(" ")}
                >
                  {l.label}
                </div>
                <p className="mt-1 text-[13px] text-[#475569]">{l.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] font-mono-display uppercase font-bold text-[#5B2E83]">
                  {isJa ? "開く" : "Go"}
                  <ArrowRight
                    className="h-3 w-3 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2}
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="text-[12px] tracking-[0.18em] font-mono-display uppercase font-bold text-[#94A3B8]">
          {isJa ? "エラーコード: 404" : "Error code: 404"}
        </div>
      </section>
    </article>
  );
}
